import React, { useState, useMemo, useCallback, useEffect } from "react";
import { StyleSheet, View, Platform, Share } from "react-native";
import { useRouter, useFocusEffect } from "expo-router";
import { MediaModel, MemoryModel } from "common247";
import ThemedScreen from "@/components/ThemedScreen";
import { ThemedView } from "@/components/ThemedView";
import MediaGallery from "@components/media/MediaGallery";
import { ThemedActionHoverPanel, type IBatchAction } from "@/components/ThemedActionHoverPanel";
import { ShareRecipientSelector } from "@/components/sharing/ShareRecipientSelector";
import { TagSelector } from "@/components/tags/TagSelector";
import { MemorySelector } from "@/components/memory/MemorySelector";
import { downloadMediaFile } from "@/utils/media-download";
import { useFirebase } from "@/components/firebase/useFirebase";
import { deleteMedia, getMyMedia } from "@/services/firebase/media-functions";
import { share } from "@/services/firebase/share-functions";
import { editMemory } from "@/services/firebase/memory-functions";
import { CustomAlert } from "@/components/ThemedAlert";
import ErrorBoundary from "@/components/ErrorBoundary";
import { CollapsibleFilterSection } from "@/components/filter";
import { useFilterPersistence } from "@/hooks/useFilterPersistence";
import { filterMedia, computeFilteredCounts } from "@/services/unified-filter-service";
import { entitySync } from "@/services/sync/entity-sync";

/**
 * Generates a default memory title from the first selected media item.
 * Formats "Memory of <cleaned first media filename>".
 * @param mediaList - Selected media items.
 * @returns Formatted title string.
 */
export function generateMemoryTitleFromMedia(mediaList: MediaModel[]): string {
  if (!mediaList || mediaList.length === 0) {
    return "Memory of Media";
  }
  const first = mediaList[0];
  const rawName = first.name || "";
  const withoutExt = rawName.replace(/\.[^/.]+$/, "");
  const cleanName = withoutExt.replace(/[-_]+/g, " ").trim();
  return cleanName ? `Memory of ${cleanName}` : "Memory of Media";
}

/**
 * Screen for displaying user media gallery with multi-select support to create memories from media.
 */
export default function MyMediaScreen() {
  const router = useRouter();
  const services = useFirebase();
  const [loading, setLoading] = useState(true);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<MediaModel[]>([]);
  const [allMediaList, setAllMediaList] = useState<MediaModel[]>([]);
  const { filter, setFilter, resetFilter } = useFilterPersistence("media");

  const effectiveMediaList = useMemo(() => {
    return filterMedia(allMediaList, filter);
  }, [allMediaList, filter]);

  const filteredCounts = useMemo(() => {
    return computeFilteredCounts({ media: allMediaList }, filter, "media");
  }, [allMediaList, filter]);

  const fetchMediaList = useCallback(
    async (showLoading = true) => {
      if (!services) return;
      if (showLoading) setLoading(true);
      try {
        const res = await getMyMedia(services, { filter });
        if (res.status && Array.isArray(res.media)) {
          setAllMediaList(res.media);
        } else if (!res.status) {
          CustomAlert.alert("Error", res.error || "Failed to load media.");
        }
      } catch (err) {
        console.error("Fetch media error:", err);
      } finally {
        if (showLoading) setLoading(false);
      }
    },
    [services, filter]
  );

  useFocusEffect(
    useCallback(() => {
      fetchMediaList(allMediaList.length === 0);
    }, [fetchMediaList, allMediaList.length])
  );

  useEffect(() => {
    const unsubscribe = entitySync.subscribe((event) => {
      if (event.type === "media") {
        if (event.action === "update" && event.data) {
          setAllMediaList((prev) =>
            prev.map((m) => (m.id === event.id ? { ...m, ...event.data } : m))
          );
        } else if (event.action === "delete") {
          setAllMediaList((prev) => prev.filter((m) => m.id !== event.id));
        } else {
          fetchMediaList(false);
        }
      }
    });
    return unsubscribe;
  }, [fetchMediaList]);
  const [isMemorySelectorOpen, setIsMemorySelectorOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [tagAnchor, setTagAnchor] = useState<{ x: number; y: number } | null>(null);

  const isSelectionActive = selectionMode || selectedMedia.length > 0;
  const selectedMediaIds = useMemo(() => selectedMedia.map((m) => m.id!).filter(Boolean), [selectedMedia]);

  const toggleSelectionMode = () => {
    if (isSelectionActive) {
      setSelectionMode(false);
      setSelectedMedia([]);
    } else {
      setSelectionMode(true);
    }
  };

  const handleClearSelection = () => {
    setSelectedMedia([]);
    setSelectionMode(false);
  };

  const handleSelectAll = () => {
    setSelectedMedia([...allMediaList]);
  };

  const handleCreateMemory = () => {
    if (selectedMedia.length === 0) return;
    const title = generateMemoryTitleFromMedia(selectedMedia);
    const mediaIds = selectedMedia.map((m) => m.id!).join(",");

    router.push({
      pathname: "/my-memories/new",
      params: {
        mediaIds,
        title,
      },
    });
  };

  const handleBatchDownload = () => {
    let count = 0;
    selectedMedia.forEach((m) => {
      if (m.downloadUrl) {
        downloadMediaFile(m.downloadUrl, m.name);
        count++;
      }
    });
    if (count === 0) {
      CustomAlert.alert("Download", "No download URLs available for the selected items.");
    }
  };

  const handleBatchSendTo = async () => {
    const urls = selectedMedia.map((m) => m.downloadUrl).filter(Boolean);
    if (urls.length === 0) {
      CustomAlert.alert("Send To", "No valid URLs found to share.");
      return;
    }
    const message = urls.join("\n");
    try {
      if (Platform.OS !== "web") {
        await Share.share({ message });
      } else if (typeof navigator !== "undefined" && (navigator as any).share) {
        await (navigator as any).share({ title: "Shared Media", text: message });
      } else {
        await Share.share({ message });
      }
    } catch (err: any) {
      console.error("Send To error:", err);
    }
  };

  const handleBatchDelete = () => {
    if (!services || selectedMedia.length === 0) return;
    CustomAlert.alert(
      "Delete Media",
      `Are you sure you want to delete ${selectedMedia.length} selected media item(s)? This cannot be undone.`,
      [
        { text: "Cancel", style: "cancel" },
        {
          text: "Delete",
          style: "destructive",
          onPress: async () => {
            let deletedCount = 0;
            for (const item of selectedMedia) {
              if (!item.id) continue;
              try {
                const res = await deleteMedia(services, { mediaId: item.id });
                if (res.status) deletedCount++;
              } catch (err) {
                console.error("Failed to delete media item:", err);
              }
            }
            CustomAlert.alert("Deleted", `Deleted ${deletedCount} media item(s).`);
            handleClearSelection();
          },
        },
      ]
    );
  };

  const handleAddMediaToExistingMemory = async (targetMemory: MemoryModel) => {
    if (!services || !targetMemory.id) return;
    const existingIds: string[] =
      targetMemory.mediaIds ||
      (Array.isArray(targetMemory.media)
        ? targetMemory.media.map((m) => m.id).filter((id): id is string => Boolean(id))
        : targetMemory.media
        ? Object.keys(targetMemory.media)
        : []) ||
      [];
    const newIds: string[] = selectedMedia.map((m) => m.id).filter((id): id is string => Boolean(id));
    const mergedIds: string[] = Array.from(new Set([...existingIds, ...newIds]));


    const res = await editMemory(services, {
      memoryId: targetMemory.id,
      mediaIds: mergedIds,
    });

    if (res.status) {
      CustomAlert.alert(
        "Media Added",
        `Successfully added ${selectedMedia.length} media item(s) to "${targetMemory.title}".`
      );
      handleClearSelection();
    } else {
      throw new Error(res.error || "Failed to update memory.");
    }
  };

  const handleBatchShareConfirm = async (
    targetId: string,
    targetType: "contact" | "group",
    role: "viewer" | "editor"
  ) => {
    if (!services) return;
    let successCount = 0;
    for (const item of selectedMedia) {
      if (!item.id) continue;
      try {
        const res = await share(services, {
          sourceId: item.id,
          sourceType: "media",
          targetId,
          targetType,
          role,
        });
        if (res.status) successCount++;
      } catch (err) {
        console.error("Failed to share media:", err);
      }
    }
    CustomAlert.alert(
      "Sharing Complete",
      `Shared ${successCount} media item(s) with ${targetType}.`
    );
    handleClearSelection();
  };

  const batchActions: IBatchAction[] = [
    {
      id: "download",
      label: "Download",
      icon: "download-outline",
      onPress: handleBatchDownload,
    },
    {
      id: "share",
      label: "Share",
      icon: "share-social-outline",
      onPress: () => setIsShareModalOpen(true),
    },
    {
      id: "sendTo",
      label: "Send To",
      icon: "paper-plane-outline",
      onPress: handleBatchSendTo,
    },
    {
      id: "tag",
      label: "Add Tag",
      icon: "pricetag-outline",
      onPress: (e?: any) => {
        if (e?.nativeEvent?.pageX !== undefined && e?.nativeEvent?.pageY !== undefined) {
          setTagAnchor({ x: e.nativeEvent.pageX, y: e.nativeEvent.pageY });
        }
        setIsTagModalOpen(true);
      },
    },
    {
      id: "addToMemory",
      label: "Add to Memory",
      icon: "bookmark-outline",
      onPress: () => setIsMemorySelectorOpen(true),
      variant: "primary",
    },
    {
      id: "delete",
      label: "Delete",
      icon: "trash-outline",
      destructive: true,
      onPress: handleBatchDelete,
      variant: "destructive",
    },
  ];

  return (
    <ThemedScreen title="Media" loading={loading} scrollable={false}>
      <ThemedView style={styles.container}>
        {/* Unified Collapsible Entity Filter */}
        <CollapsibleFilterSection
          screen="media"
          filter={filter}
          onFilterChange={setFilter}
          counts={filteredCounts}
          onReset={resetFilter}
          allMedia={allMediaList}
        />

        <View style={{ flex: 1, minHeight: 0 }}>
          <ErrorBoundary>
            <MediaGallery
              data={effectiveMediaList}
              selectionMode={isSelectionActive}
              useThemedList={true}
              selectedIds={selectedMediaIds}
              onSelect={setSelectedMedia}
              onLoadingChange={setLoading}
              onMediaListChange={setAllMediaList}
              onRefresh={fetchMediaList}
              showProgressBar={false}
              skipAutoMemory={true}
              addButton={{
                title: isSelectionActive ? "Back" : "Select",
                variant: isSelectionActive ? "primary" : "secondary",
                onPress: toggleSelectionMode,
              }}
            />
          </ErrorBoundary>
        </View>

        {/* Floating Contextual Batch Action Panel */}
        <ThemedActionHoverPanel
          visible={selectedMedia.length > 0}
          selectedCount={selectedMedia.length}
          totalCount={allMediaList.length}
          onSelectAll={handleSelectAll}
          onClearSelection={handleClearSelection}
          actions={batchActions}
        />

        {/* Hover/Popup Memory Selector */}
        <MemorySelector
          visible={isMemorySelectorOpen}
          onClose={() => setIsMemorySelectorOpen(false)}
          selectedCount={selectedMedia.length}
          itemTypeLabel="media"
          onSelectMemory={handleAddMediaToExistingMemory}
          onCreateNewMemory={handleCreateMemory}
        />

        {/* Batch Share Modal */}
        <ShareRecipientSelector
          visible={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          selectedCount={selectedMedia.length}
          itemTypeLabel="media"
          onConfirmShare={handleBatchShareConfirm}
        />

        {/* Batch Tag Selector Modal */}
        <TagSelector
          visible={isTagModalOpen}
          anchorPosition={tagAnchor}
          onClose={() => setIsTagModalOpen(false)}
          entityType="media"
          entityIds={selectedMediaIds}
          onTagsBatchAdded={() => {
            CustomAlert.alert(
              "Tags Assigned",
              `Tag assigned to ${selectedMedia.length} media item(s).`
            );
            handleClearSelection();
          }}
        />
      </ThemedView>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 8,
    paddingBottom: 0,
    minHeight: 0,
  },
});
